import { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import CreatableSelect from "react-select/creatable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";

export default function DoctorUpdateProfile() {
  const [formData, setFormData] = useState({
    name: "",
    profile: "", // preview url OR existing cdn url
    description: "",
    specialisation: "",
    treatmentIds: [],
    hospitalIds: [],
    appointments: [],
    availableDays: [],
  });

  const [profileFile, setProfileFile] = useState(null); // ✅ NEW
  const [treatments, setTreatments] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [slotStart, setSlotStart] = useState("");
  const [slotEnd, setSlotEnd] = useState("");
  const [slotMax, setSlotMax] = useState("");

  const DAYS = [
    "MONDAY","TUESDAY","WEDNESDAY",
    "THURSDAY","FRIDAY","SATURDAY","SUNDAY"
  ];

  useEffect(() => {
    loadMeta();
    loadDoctorProfile();
  }, []);

  async function loadMeta() {
    try {
      const [tResp, hResp] = await Promise.all([
        axiosInstance.get("/profile/treatments"),
        axiosInstance.get("/profile/hospitals"),
      ]);

      setTreatments(tResp.data.map(t => ({ value: t.id, label: t.name })));
      setHospitals(hResp.data.map(h => ({ value: h.id, label: h.name })));
    } catch {
      toast.error("Failed to load metadata");
    }
  }

  async function loadDoctorProfile() {
    try {
      const res = await axiosInstance.get("/profile/doctor");
      if (!res.data.data) return;

      const d = res.data.data;

      setFormData({
        name: d.name || "",
        profile: d.profile || "", // CDN URL
        description: d.description || "",
        specialisation: d.specialisation || "",
        treatmentIds: d.treatments?.map(t => t.id) || [],
        hospitalIds: d.hospitals?.map(h => h.id) || [],
        appointments: d.appointments || [],
        availableDays: d.availableDays?.length
          ? d.availableDays
          : DAYS.map(day => ({ day, available: false }))
      });
    } catch (err) {
      console.error(err);
    }
  }

  function formatLocalDate(d) {
    return d.toLocaleDateString("en-CA"); // YYYY-MM-DD
  }

  function handleAddSlot() {
    if (!slotStart || !slotEnd || selectedDates.length === 0) {
      toast.error("Pick dates and select start/end time");
      return;
    }

    const newSlots = selectedDates.map(d => ({
      date: formatLocalDate(d),
      start: slotStart,
      end: slotEnd,
      maxSlots: Number(slotMax),
      filledSlots: 0,
    }));

    setFormData(p => ({
      ...p,
      appointments: [...p.appointments, ...newSlots],
    }));

    toast.success("Slots added!");
    setSelectedDates([]);
    setSlotStart("");
    setSlotEnd("");
    setSlotMax("");
  }

  function removeSlot(i) {
    setFormData(p => ({
      ...p,
      appointments: p.appointments.filter((_, idx) => idx !== i),
    }));
  }

  async function handleAddTreatment(newName) {
    try {
      const res = await axiosInstance.post("/profile/add-treatment", {
        name: newName,
        description: "",
      });
      const item = { value: res.data.id, label: res.data.name };
      setTreatments(p => [...p, item]);
      toast.success("Treatment added!");
      return item;
    } catch {
      toast.error("Failed to add treatment");
    }
  }

  async function handleAddHospital(newName) {
    try {
      const res = await axiosInstance.post("/profile/add-hospital", {
        name: newName,
        address: "",
      });
      const item = { value: res.data.id, label: res.data.name };
      setHospitals(p => [...p, item]);
      toast.success("Hospital added!");
      return item;
    } catch {
      toast.error("Failed to add hospital");
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.name) {
      toast.error("Name and Pincode are required");
      return;
    }

    try {
      const fd = new FormData();

      fd.append("name", formData.name);
      fd.append("description", formData.description);
      fd.append("specialisation", formData.specialisation);

      fd.append("treatmentIds", JSON.stringify(formData.treatmentIds));
      fd.append("hospitalIds", JSON.stringify(formData.hospitalIds));
      fd.append("appointments", JSON.stringify(formData.appointments));
      fd.append("availableDays", JSON.stringify(formData.availableDays));

      if (profileFile) {
        fd.append("profile", profileFile); // Multipart file
      }

      await axiosInstance.post("/profile/update-doctor", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Profile updated!");
    } catch {
      toast.error("Failed to update");
    }
  }

  return (
    <div className="max-w-3xl mx-auto bg-white p-8 rounded-xl shadow">
      <h2 className="text-2xl font-bold mb-6">Doctor Profile Update</h2>

      <form className="space-y-6" onSubmit={handleSubmit}>
        {/* Basic Fields */}
        <div>
          <label className="font-medium">Name</label>
          <input
            className="w-full border px-4 py-2 rounded mt-1"
            value={formData.name}
            onChange={(e) =>
              setFormData((p) => ({ ...p, name: e.target.value }))
            }
          />
        </div>

        <div>
          <label className="font-medium">Profile Image</label>
          <input
            type="file"
            className="w-full border px-4 py-2 rounded mt-1"
            accept="image/*"
            onChange={e => {
              const file = e.target.files[0];
              if (!file) return;
              setProfileFile(file);
              setFormData(p => ({
                ...p,
                profile: URL.createObjectURL(file),
              }));
            }}
          />

          {formData.profile && (
            <img
              src={formData.profile}
              alt="Preview"
              className="w-32 h-32 rounded-full mt-3 object-cover border"
            />
          )}
        </div>

        <div>
          <label className="font-medium">Description</label>
          <textarea
            className="w-full border px-4 py-2 rounded mt-1"
            rows={4}
            value={formData.description}
            onChange={(e) =>
              setFormData((p) => ({ ...p, description: e.target.value }))
            }
          ></textarea>
        </div>

        <div>
          <label className="font-medium">Specialisation</label>
          <input
            className="w-full border px-4 py-2 rounded mt-1"
            value={formData.specialisation}
            onChange={(e) =>
              setFormData((p) => ({ ...p, specialisation: e.target.value }))
            }
          />
        </div>

        {/* Treatments */}
        <div>
          <label className="font-medium">Treatments</label>
          <CreatableSelect
            isMulti
            options={treatments}
            value={treatments.filter(t => formData.treatmentIds.includes(t.value))}
            onCreateOption={handleAddTreatment}
            formatCreateLabel={(input) => `Add "${input}"`}
            onChange={(sel) =>
              setFormData((p) => ({
                ...p,
                treatmentIds: sel.map((x) => x.value),
              }))
            }
            className="mt-1"
          />
        </div>

        {/* Hospitals */}
        <div>
          <label className="font-medium">Hospitals</label>
          <CreatableSelect
            isMulti
            options={hospitals}
            value={hospitals.filter(h => formData.hospitalIds.includes(h.value))}
            onCreateOption={handleAddHospital}
            formatCreateLabel={(input) => `Add "${input}"`}
            onChange={(sel) =>
              setFormData((p) => ({
                ...p,
                hospitalIds: sel.map((x) => x.value),
              }))
            }
            className="mt-1"
          />
        </div>

       {/* Available Days Section */}
        <div>
          <label className="font-medium block mb-2">Available Days</label>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {DAYS.map((day) => {
              const isChecked = formData.availableDays.some(
                (d) => d.day === day && d.available
              );

              return (
                <label key={day} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      const updated = DAYS.map((d) =>
                        d === day
                          ? { day, available: e.target.checked }
                          : formData.availableDays.find((x) => x.day === d) || {
                              day: d,
                              available: false,
                            }
                      );

                      setFormData((p) => ({
                        ...p,
                        availableDays: updated,
                      }));
                    }}
                  />
                  {day}
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <label className="font-medium block mb-2">Pick Appointment Dates</label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {/* Calendar */}
            <div>
              <DatePicker
                inline
                selected={null}
                onChange={(date) => {
                  if (!date) return;

                  const exists = selectedDates.some(
                    (d) => d.toDateString() === date.toDateString()
                  );

                  if (exists) {
                    setSelectedDates((prev) =>
                      prev.filter((d) => d.toDateString() !== date.toDateString())
                    );
                  } else {
                    setSelectedDates((prev) => [...prev, date]);
                  }
                }}
                dayClassName={(date) =>
                  selectedDates.some(
                    (d) => d.toDateString() === date.toDateString()
                  )
                    ? "bg-blue-600 text-white rounded-full"
                    : undefined
                }
              />
            </div>

            {/* Slot Inputs */}
            <div className="space-y-4 border rounded p-4 shadow-sm">
              <div>
                <label className="block font-medium">Start Time</label>
                <input
                  type="time"
                  className="border px-3 py-2 rounded w-full"
                  value={slotStart}
                  onChange={(e) => setSlotStart(e.target.value)}
                />
              </div>

              <div>
                <label className="block font-medium">End Time</label>
                <input
                  type="time"
                  className="border px-3 py-2 rounded w-full"
                  value={slotEnd}
                  onChange={(e) => setSlotEnd(e.target.value)}
                />
              </div>

              <div>
                <label className="block font-medium">Max Count</label>
                <input
                  type="number"
                  min="1"
                  className="border px-3 py-2 rounded w-full"
                  value={slotMax}
                  onChange={(e) => setSlotMax(e.target.value)}
                />
              </div>

              <button
                type="button"
                className="bg-blue-700 text-white w-full py-2 rounded-lg"
                onClick={handleAddSlot}
              >
                Add Slot
              </button>
            </div>
          </div>
        </div>

        {/* Added Slots */}
        <div>
          <h3 className="font-semibold mb-2">Added Slots</h3>

          {formData.appointments.length === 0 && (
            <p className="text-gray-500">No slots added</p>
          )}

          <ul className="space-y-3">
            {formData.appointments.map((s, i) => (
              <li
                key={i}
                className="flex justify-between border p-3 rounded shadow-sm"
              >
                <span>
                  <b>{s.date}</b> — {s.start} to {s.end}
                  <span className="ml-3 text-sm bg-gray-200 px-2 py-1 rounded">
                    Max: {s.maxSlots}
                  </span>
 
                </span>
                <button
                  type="button"
                  className="text-red-600"
                  onClick={() => removeSlot(i)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="bg-blue-700 text-white px-4 py-2 w-full rounded-lg"
        >
          Update Profile
        </button>
      </form>
    </div>
  );
}
